import styled from '@emotion/styled'
import React from 'react'

import { Dayjs, dayjsEn } from '@bemlo/date'

import { DayCellStyles } from './types'
import { Color, theme } from '../../../constants'
import {
  Clickable,
  Div,
  Icon,
  IconDesign,
  IconName,
  Text,
} from '../../../elements'
import { AvailabilityDayStatus } from '../overview/types'
import { AvailabilityDay } from '../types'
import {
  BOOKED_DAY_STYLE,
  DISABLED_DAY_STYLE,
  REJECTED_DAY_STYLE,
  VIEW_TENDERED_DAY_STYLE,
} from '../utils/constants'

export const BaseCell = styled(Div)<{ disabled?: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-left: ${theme.border.style.thinLight};
`

const BodyCell = styled(BaseCell)<{
  color: Color
  backgroundColor: Color
}>`
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`

export type GetDayCellStatus = (
  day: AvailabilityDay,
  weekdayDisabled: boolean,
  viewState: 'edit' | 'view',
) => DayCellStyles & {
  isDaySelectable?: boolean
}

type DayCellProps = {
  onClick?: () => void
  day: AvailabilityDay
  viewState: 'edit' | 'view'
  weekDisabled?: boolean
  getDayCellStatus: GetDayCellStatus
}

export const DayCell = ({
  onClick,
  day,
  viewState,
  weekDisabled,
  getDayCellStatus,
}: DayCellProps) => {
  const { color, backgroundColor, icon, isDaySelectable } = getDayCellStatus(
    day,
    !!weekDisabled,
    viewState,
  )

  return (
    <BodyCell
      as={onClick ? Clickable : undefined}
      onClick={onClick}
      disabled={!isDaySelectable}
      color={color}
      backgroundColor={backgroundColor}
    >
      {day.date.format('DD')}
      {icon && (
        <Icon design={icon.design} name={icon.name} color={color} size="sm" />
      )}
    </BodyCell>
  )
}

export const DayCellHeader = ({
  index,
  children,
}: React.PropsWithChildren<{
  index: number
}>) => (
  <BaseCell>
    <Text bold>
      {dayjsEn()
        .day(index + 1)
        .format('ddd')}
    </Text>
    {children}
  </BaseCell>
)

export const OverviewDayCellHeader = ({
  date,
  status,
}: {
  date: Dayjs
  status: 'tendered' | 'none'
}) => {
  return (
    <BaseCell styles={status === 'none' ? DISABLED_DAY_STYLE : undefined}>
      <Text>{date.format('ddd')}</Text>
      <Text>{date.format('DD')}</Text>
    </BaseCell>
  )
}

const getOverviewBodyCellStyles = (status: AvailabilityDayStatus) => {
  switch (status) {
    case 'none':
      return DISABLED_DAY_STYLE
    case 'tendered':
      return VIEW_TENDERED_DAY_STYLE
    case 'presented': {
      return {
        color: Color.DARK_YELLOW,
        backgroundColor: Color.YELLOW,
        icon: {
          design: 'outline' as IconDesign,
          name: 'QuestionMarkCircleIcon' as IconName,
        },
      }
    }
    case 'booked':
      return BOOKED_DAY_STYLE
    case 'rejected':
      return REJECTED_DAY_STYLE
  }
}

export const OverviewBodyCell = ({
  status,
}: {
  status: 'none' | 'tendered' | 'presented' | 'booked' | 'rejected'
}) => {
  const { color, backgroundColor, icon } = getOverviewBodyCellStyles(status)
  return (
    <BaseCell styles={{ color, backgroundColor }}>
      {icon && (
        <Icon design={icon.design} name={icon.name} color={color} size="md" />
      )}
    </BaseCell>
  )
}
